import {
  type Palette,
  type PreparedShape,
  type Rng,
  type ShapeDefinition,
  type ShapeSink,
} from '../shape.js';
import { onSphere, TAU } from './helpers.js';
import { jellyfish } from './life.js';

/*
 * A whole family of jellyfish for the dedicated jellyfish visualiser: four
 * species with their own silhouettes and palettes, plus "blooms" of two, three
 * and four smaller jellies. Because the director morphs between everything in
 * its registry, cycling this family is what makes one jelly split into a bloom
 * and a bloom combine back into a single animal.
 */

interface JellySpecies {
  readonly name: string;
  readonly palette: Palette;
  /** Bell silhouette: radius multiplier at normalised height v (0 rim, 1 crown). */
  readonly profile: (v: number) => number;
  readonly bellRadius: number;
  readonly bellHeight: number;
  /** Normalised height of the luminous ring around the bell. */
  readonly rimHeight: number;
  readonly strands: number;
  readonly tentacleLength: number;
  /** How far the frilled oral arms hang below the bell. */
  readonly armDrop: number;
  /** Particle budget: bell / glow ring / oral arms; tentacles take the rest. */
  readonly weights: { readonly bell: number; readonly rim: number; readonly arms: number };
}

const MOON: JellySpecies = {
  name: 'moon jelly',
  palette: { base: 0x01_06_1c, shade: 0x00_d4_ff, accent: 0xff_14_93 },
  profile: (v) => Math.sin((1 - v) * Math.PI * 0.5) ** 0.55,
  bellRadius: 4.2,
  bellHeight: 2.1,
  rimHeight: 0,
  strands: 44,
  tentacleLength: 6,
  armDrop: 3.5,
  weights: { bell: 0.54, rim: 0.1, arms: 0.15 },
};

const LIONS_MANE: JellySpecies = {
  name: 'lions mane',
  palette: { base: 0x14_00_18, shade: 0xff_3c_00, accent: 0x00_ff_ee },
  profile: (v) => Math.sin((1 - v) * Math.PI * 0.5),
  bellRadius: 3.4,
  bellHeight: 3.1,
  rimHeight: 0,
  strands: 46,
  tentacleLength: 14,
  armDrop: 5,
  weights: { bell: 0.36, rim: 0.08, arms: 0.16 },
};

const BOX: JellySpecies = {
  name: 'box jelly',
  palette: { base: 0x00_14_1c, shade: 0x00_ff_88, accent: 0xff_e6_00 },
  profile: (v) => Math.cos(v * Math.PI * 0.5) ** 0.25,
  bellRadius: 2.4,
  bellHeight: 3.8,
  rimHeight: 0,
  strands: 8,
  tentacleLength: 11,
  armDrop: 3.8,
  weights: { bell: 0.5, rim: 0.1, arms: 0.12 },
};

const COMB: JellySpecies = {
  name: 'comb jelly',
  palette: { base: 0x18_02_24, shade: 0xa8_00_ff, accent: 0x00_ff_d4 },
  profile: (v) => Math.sin(v * Math.PI) ** 0.8,
  bellRadius: 2.7,
  bellHeight: 4.6,
  rimHeight: 0.45,
  strands: 4,
  tentacleLength: 8,
  armDrop: 1.5,
  weights: { bell: 0.6, rim: 0.12, arms: 0.08 },
};

const SPECIES: readonly JellySpecies[] = [MOON, LIONS_MANE, BOX, COMB];

/** One jellyfish placed somewhere in the composition, at its own size and beat. */
interface JellyInstance {
  readonly species: JellySpecies;
  readonly scale: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly strands: number;
  /** Pulse phase offset, so the members of a bloom never throb in unison. */
  readonly beatPhase: number;
  /** Pushes this jelly's colours towards the accent end of the palette. */
  readonly tintBias: number;
}

/*
 * The four regions of a jellyfish, all in local coordinates with the rim of the
 * bell on the y = 0 plane. Every region's drift is the pulse: the bell squeezes
 * radially while the arms and tentacles trail behind with a travelling wave,
 * which is what makes the animal read as swimming rather than wobbling.
 */

function placeBell(sink: ShapeSink, random: Rng, s: JellySpecies): void {
  const v = random();
  const angle = random() * TAU;
  const shellLayer = random() > 0.35 ? 1 : 0.94 + random() * 0.05;
  const r = s.profile(v) * s.bellRadius * shellLayer;
  sink.x = Math.cos(angle) * r;
  sink.z = Math.sin(angle) * r;
  sink.y = v * s.bellHeight;
  const filament = Math.sin(angle * 8 + v * 6) * 0.12;
  sink.tint = 0.28 + (1 - v) * 0.35 + filament + random() * 0.04;
  sink.driftX = sink.x * 0.17;
  sink.driftZ = sink.z * 0.17;
  sink.driftY = -0.35 * (1 - v);
  sink.phase = v * 0.8;
}

/**
 * A solid, luminous aurora ring defining the edge of the bell.
 */
function placeRim(sink: ShapeSink, random: Rng, s: JellySpecies): void {
  const angle = random() * TAU;
  const bleed = (random() - 0.5) * 0.45;
  const r = s.profile(s.rimHeight) * s.bellRadius + bleed;
  sink.x = Math.cos(angle) * r;
  sink.z = Math.sin(angle) * r;
  sink.y = s.rimHeight * s.bellHeight + (random() - 0.5) * 0.35;
  sink.tint = 0.75 + random() * 0.22;
  sink.driftX = sink.x * 0.17;
  sink.driftZ = sink.z * 0.17;
  sink.driftY = -0.15;
  sink.phase = s.rimHeight * 0.8 + random() * 0.4;
}

/** Faint motes drifting around the animal, like it swims through a nebula. */
function placeDust(sink: ShapeSink, random: Rng, s: JellySpecies): void {
  onSphere(sink, random, s.bellRadius * (1.4 + random() * 1.8));
  sink.y = sink.y * 1.4 + s.bellHeight * 0.3 - random() * s.tentacleLength * 0.5;
  sink.tint = 0.8 + random() * 0.2;
  sink.driftX = 0.4;
  sink.driftY = 0.3;
  sink.driftZ = 0.4;
  sink.phase = random() * TAU;
}

/** Billowing ethereal plasma smoke plumes streaming out of the jellyfish. */
function placeSmoke(sink: ShapeSink, random: Rng, s: JellySpecies): void {
  const u = random();
  const angle = random() * TAU;
  const plumeSpread = Math.sin(u * Math.PI) * (1.2 + random() * 1.8);
  const r = (s.bellRadius * 0.7 + plumeSpread) * (0.8 + u * 1.1);

  // Spiral vortex turbulence
  const vortexAngle = angle + u * 3.5;
  sink.x = Math.cos(vortexAngle) * r;
  sink.z = Math.sin(vortexAngle) * r;
  sink.y = u * (s.bellHeight * 2.2) - (random() - 0.5) * 1.5;
  sink.tint = 0.72 + random() * 0.28;

  // Dynamic fluid smoke drift: buoyant upward lift + outward vortex swirl
  const swirl = 0.7 + u * 0.9;
  sink.driftX = Math.cos(vortexAngle + 1.57) * swirl + Math.sin(u * 8) * 0.4;
  sink.driftZ = Math.sin(vortexAngle + 1.57) * swirl + Math.cos(u * 8) * 0.4;
  sink.driftY = 0.65 + u * 0.8;
  sink.phase = vortexAngle + u * 5;
}

function placeArms(sink: ShapeSink, random: Rng, s: JellySpecies): void {
  const angle = random() * TAU;
  const down = random();
  const armRootRadius = s.bellRadius * 0.38;
  const wave = Math.sin(down * 5.5 + angle * 3.5) * down * 0.65;
  const r = armRootRadius + wave;
  sink.x = Math.cos(angle) * r;
  sink.z = Math.sin(angle) * r;
  sink.y = -down * s.armDrop;
  sink.tint = 0.35 + down * 0.45 + (random() - 0.5) * 0.08;

  // Base drift matches the bell underside
  const baseDriftX = sink.x * 0.17;
  const baseDriftZ = sink.z * 0.17;
  const baseDriftY = -0.35;
  const basePhase = 0;

  const armPhase = angle * 2.5 + down * 4.5;
  sink.driftX = baseDriftX * (1 - down) + (baseDriftX + Math.cos(armPhase) * 0.7) * down;
  sink.driftZ = baseDriftZ * (1 - down) + (baseDriftZ + Math.sin(armPhase) * 0.7) * down;
  sink.driftY = baseDriftY * (1 - down) + (baseDriftY - down * 0.4) * down;
  sink.phase = basePhase * (1 - down) + armPhase * down;
}

function placeTentacles(sink: ShapeSink, random: Rng, s: JellySpecies, strands: number): void {
  const strand = Math.floor(random() * strands);
  const angle = (strand / strands) * TAU;
  const down = random();
  const rimRadius = s.profile(s.rimHeight) * s.bellRadius;

  // Each strand has a unique golden-ratio phase and independent wave frequency
  const strandPhase = strand * 2.3999 + strand * 1.618;
  const freq1 = 5.2 + (strand % 5) * 0.75;
  const freq2 = 4.1 + (strand % 4) * 0.85;

  const curlX = Math.sin(down * freq1 + strandPhase) * down * (1.1 + Math.sin(strand * 1.7) * 0.35);
  const curlZ =
    Math.cos(down * freq2 + strandPhase * 1.25) * down * (1.1 + Math.cos(strand * 2.3) * 0.35);

  sink.x = Math.cos(angle) * rimRadius + curlX;
  sink.z = Math.sin(angle) * rimRadius + curlZ;
  sink.y = s.rimHeight * s.bellHeight - down * s.tentacleLength;
  sink.tint = 0.55 + down * 0.4 + Math.sin(down * 10 + strandPhase) * 0.05;

  // Anchor lock: at down = 0, drift and phase strictly match the bell rim
  const baseDriftX = Math.cos(angle) * rimRadius * 0.17;
  const baseDriftZ = Math.sin(angle) * rimRadius * 0.17;
  const baseDriftY = -0.35 * (1 - s.rimHeight);
  const basePhase = s.rimHeight * 0.8;

  // Independent multi-frequency undulating wave for each strand
  const waveX = Math.sin(down * freq1 + strandPhase) * 1.25;
  const waveZ = Math.cos(down * freq2 + strandPhase * 1.3) * 1.25;
  const waveY = Math.sin(down * 3.8 + strandPhase * 0.8) * 0.5 - 0.45;

  sink.driftX = baseDriftX * (1 - down) + (baseDriftX + waveX) * down;
  sink.driftZ = baseDriftZ * (1 - down) + (baseDriftZ + waveZ) * down;
  sink.driftY = baseDriftY * (1 - down) + (baseDriftY + waveY) * down;
  sink.phase = basePhase * (1 - down) + (strandPhase + down * 4.5) * down;
}

/** Thresholds for smoke and dust particle partitions. */
const SMOKE_FROM = 0.84;
const DUST_FROM = 0.94;

/** Place one particle of one jellyfish. `roll` in [0, 1) picks the region. */
function placeJelly(sink: ShapeSink, random: Rng, jelly: JellyInstance, roll: number): void {
  const s = jelly.species;
  const { bell, rim, arms } = s.weights;
  if (roll < bell) {
    placeBell(sink, random, s);
  } else if (roll < bell + rim) {
    placeRim(sink, random, s);
  } else if (roll < bell + rim + arms) {
    placeArms(sink, random, s);
  } else if (roll < SMOKE_FROM) {
    placeTentacles(sink, random, s, jelly.strands);
  } else if (roll < DUST_FROM) {
    placeSmoke(sink, random, s);
  } else {
    placeDust(sink, random, s);
  }
  sink.x = sink.x * jelly.scale + jelly.x;
  sink.y = sink.y * jelly.scale + jelly.y;
  sink.z = sink.z * jelly.scale + jelly.z;
  sink.driftX *= jelly.scale;
  sink.driftY *= jelly.scale;
  sink.driftZ *= jelly.scale;
  sink.phase += jelly.beatPhase;
  sink.tint = Math.min(1, sink.tint + jelly.tintBias);
}

function strandCount(species: JellySpecies, factor: number): number {
  return Math.max(2, Math.round(species.strands * factor));
}

function soloJelly(species: JellySpecies, framing: number): ShapeDefinition {
  return {
    name: species.name,
    palette: species.palette,
    driftRate: 1.05,
    framing,
    prepare: ({ random, total, variant }): PreparedShape => {
      const jelly: JellyInstance = {
        species,
        scale: 0.9 + variant * 0.35,
        x: 0,
        y: 3,
        z: 0,
        strands: strandCount(species, 0.8 + variant * 0.6),
        beatPhase: 0,
        tintBias: 0,
      };
      const place = (index: number, sink: ShapeSink): void => {
        placeJelly(sink, random, jelly, index / total);
      };
      return { place };
    },
  };
}

function pickSpecies(random: Rng): JellySpecies {
  return SPECIES[Math.floor(random() * SPECIES.length)] ?? MOON;
}

/**
 * A bloom of several smaller jellies drifting together. Each member gets its
 * own species, size, depth and pulse phase, and a contiguous block of the
 * particle budget — so a morph into a single jelly visibly gathers them into
 * one animal, and a morph the other way is that animal dividing.
 */
function jellyBloom(name: string, count: number, palette: Palette): ShapeDefinition {
  return {
    name,
    palette,
    driftRate: 0.95,
    framing: 2.2,
    prepare: ({ random, total, variant }): PreparedShape => {
      const ringRadius = 4.5 + count * 1.4 + variant * 2;
      const members: JellyInstance[] = [];
      for (let j = 0; j < count; j += 1) {
        const species = pickSpecies(random);
        const angle = (j / count) * TAU + random() * 0.9;
        const strands = strandCount(species, 0.6 + random() * 0.5);
        members.push({
          species,
          scale: 0.5 + random() * 0.28,
          x: Math.cos(angle) * ringRadius,
          y: 2 + (random() - 0.5) * 5,
          z: Math.sin(angle) * ringRadius * 0.8,
          strands,
          beatPhase: j * 1.9 + random(),
          tintBias: (j / count) * 0.2,
        });
      }
      const place = (index: number, sink: ShapeSink): void => {
        const p = index / total;
        const slot = Math.min(count - 1, Math.floor(p * count));
        const member = members[slot];
        if (member === undefined) {
          return;
        }
        placeJelly(sink, random, member, p * count - slot);
      };
      return { place };
    },
  };
}

const moonJelly = soloJelly(MOON, 2.6);
const lionsMane = soloJelly(LIONS_MANE, 2.4);
const boxJelly = soloJelly(BOX, 2.7);
const combJelly = soloJelly(COMB, 2.8);

const jellyPair = jellyBloom('jelly pair', 2, {
  base: 0x02_08_28,
  shade: 0x00_99_ff,
  accent: 0xff_00_7f,
});
const jellyTrio = jellyBloom('jelly trio', 3, {
  base: 0x18_00_20,
  shade: 0xff_00_66,
  accent: 0x00_ff_cc,
});
const jellyQuartet = jellyBloom('jelly quartet', 4, {
  base: 0x01_14_1e,
  shade: 0x00_e5_ff,
  accent: 0xff_bb_00,
});

/**
 * The registry for the jellyfish page: only jellyfish, forever. Singles morph
 * into other species, sometimes divide into blooms of up to four, and every
 * visit arrives re-seeded and re-coloured.
 */
export const JELLY_SHAPES: readonly ShapeDefinition[] = [
  jellyfish,
  moonJelly,
  lionsMane,
  boxJelly,
  combJelly,
  jellyPair,
  jellyTrio,
  jellyQuartet,
];
